import { ClipboardCheck, PieChart03, BarChart12 } from '@untitledui/icons'
import { Link } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { Badge } from '@components/designSystem/badges/badges'
import { useCurrentStoreId } from '@hooks/useCurrentStore'
import { usePageHeader } from '@hooks/useHeader'
import { useScan } from '@hooks/useScan'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { routesFactory } from '@utils/routes'

interface AnalyticsCardProps {
  to?: string
  title: string
  description: string
  icon: React.ReactNode
  enabled: boolean
}

function AnalyticsLandingRoute() {
  const { t } = useTypedTranslation()
  const storeId = useCurrentStoreId()
  const { pathParam: scanId } = useScan()

  usePageHeader({ title: t('analytics.title'), showQuickSelection: false })

  return (
    <div className='flex h-full flex-col gap-6 overflow-auto'>
      <div className='flex flex-col gap-2'>
        <h2 className='text-xl font-semibold'>{t('analytics.landing.title')}</h2>
        <p className='text-secondary'>{t('analytics.landing.description')}</p>
      </div>

      <div className='flex flex-col gap-6 md:flex-row'>
        <AnalyticsCard
          to={routesFactory.analyticsShelfScanReport(storeId, scanId)}
          title={t('analytics.landing.shelfScanReport.title')}
          description={t('analytics.landing.shelfScanReport.description')}
          icon={<BarChart12 size={48} />}
          enabled={true}
        />

        <AnalyticsCard
          title={t('analytics.landing.taskStatistics.title')}
          description={t('analytics.landing.taskStatistics.description')}
          icon={<ClipboardCheck size={48} />}
          enabled={false}
        />

        <AnalyticsCard
          title={t('analytics.landing.productGaps.title')}
          description={t('analytics.landing.productGaps.description')}
          icon={<PieChart03 size={48} />}
          enabled={false}
        />
      </div>
    </div>
  )
}

function AnalyticsCard({ to, title, description, icon, enabled }: AnalyticsCardProps) {
  const { t } = useTypedTranslation()

  const content = (
    <>
      <div
        className={twJoin(
          'flex size-24 items-center justify-center rounded-xl',
          !enabled ? 'bg-gray-100' : 'bg-brand-50'
        )}
      >
        <div className={!enabled ? 'text-text-tertiary' : 'text-brand-600'}>
          {icon}
        </div>
      </div>

      <div className='flex flex-col items-center gap-1 text-center'>
        <h3 className='text-lg font-semibold text-text-primary'>{title}</h3>
        <p className='text-sm text-secondary'>{description}</p>
      </div>

      {!enabled && <Badge>{t('analytics.landing.comingSoon')}</Badge>}
    </>
  )

  const className = `
    flex flex-1 flex-col items-center gap-4 rounded-xl border border-border-secondary bg-white p-6 shadow-sm
    transition-all
    ${!enabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-brand-300 hover:shadow-md'}
  `

  if (!enabled || !to) {
    return <div className={className}>{content}</div>
  }

  return (
    <Link to={to} className={className}>
      {content}
    </Link>
  )
}

export default AnalyticsLandingRoute
